import { Component, Inject, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MenuItem } from 'primeng/api';
import { AuthService } from '@auth0/auth0-angular';
import { PermissionsStoreService } from '../../services/stores/permission-store-service';
import { Subscription, firstValueFrom } from 'rxjs';
import { UserPermissions } from '../../domain/security/user-permissions';
import { WidgetStoreService } from '../../services/stores/widget-store.service';
import { WidgetType } from '../../services/util/widget-type';
import { MentionStoreService } from '../../services/stores/mention-store.service';
import { Router } from '@angular/router';
import { TaskUtils } from '../../util/task-utils';
import { Property } from '../../domain/property';
import { environment } from '../../../environments/environment';
import { BillingService } from '../../services/billing.service';
import { PropertyStoreService } from '../../services/stores/property-store.service';

type TrialPhase = 'NONE' | 'TRIAL' | 'GRACE' | 'EXPIRED';
@Component({
   selector: 'app-topbar',
   templateUrl: './top-bar.component.html',
   styleUrls: ['./top-bar.component.scss']
})
export class TopBarComponent implements OnInit {
   private permsSub?: Subscription;
   mainMenuItems: MenuItem[] = [];
   userPermissions: UserPermissions;
   outstandingCount: number = 0;
   mentionCount: number = 0;
   userEmail: string = '';
   currentProperty: Property | null = null;
   outstandingTaskList: any[] = [];
   environment = environment;
   trialPhase: TrialPhase = 'NONE';
   showTrialNotice: boolean = false;
   showGraceModal: boolean = false;
   showTrialBadge: boolean = false;
   daysRemaining: number = 0;
   isGraceExpired: boolean = false;
   graceDaysRemaining: number = 0;
   daysPastTrial: number = 0;
   billingId: string | null = null;
   private hasLoadedPermissions = false;
   constructor(
      @Inject(DOCUMENT) private document: Document,
      public auth: AuthService,
      private permissionsStore: PermissionsStoreService,
      private widgetStore: WidgetStoreService,
      private mentionStore: MentionStoreService,
      private propStore: PropertyStoreService,
      private billingService: BillingService,
      private router: Router
   ) {
      this.userPermissions = new UserPermissions();
   }

   async ngOnInit(): Promise<void> {
      this.auth.user$.subscribe((user) => {
         if (user?.email) {
            this.userEmail = user.email;
            this.tryCreateMenus();
         }
      });

      this.permsSub = this.permissionsStore.get().subscribe((perms) => {
         this.userPermissions = perms;
         this.hasLoadedPermissions = true;
         this.tryCreateMenus();
      });

      this.userPermissions = await firstValueFrom(this.permissionsStore.get());
      this.hasLoadedPermissions = true;
      this.tryCreateMenus();

      this.widgetStore.widgets.subscribe((widgets) => {
         const claimed = widgets.find((w) => w.type === WidgetType.ClaimedTasks);
         if (claimed) {
            this.outstandingCount = claimed.count;
            this.outstandingTaskList = claimed.data;
         }
      });

      this.propStore.currentProperty.subscribe(async (property) => {
         this.currentProperty = property;
         if (property) {
            await this.evaluateTrialNotice(property);
         }
      });

      this.mentionStore.mentions.subscribe((mentions) => {
         this.mentionCount = mentions.length;
      });
   }

   async evaluateTrialNotice(property: Property) {
      this.trialPhase = 'NONE';
      this.showTrialNotice = false;
      this.showGraceModal = false;
      this.showTrialBadge = false;

      // Not on trial → nothing
      if (!property.isOnTrial) {
         return;
      }

      // Check billing
      const billing = await this.billingService
         .getPropertyBillingSummary(property.id)
         .toPromise()
         .catch(() => null);

      this.billingId = billing?.billingId ?? null;

      // Billing exists → nothing
      if (this.billingId) {
         return;
      }

      const created = new Date(property.createdDate!);
      const today = new Date();
      const daysLive = Math.floor(
         (today.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)
      );

      const graceStart = environment.trialDays;
      const graceEnd = graceStart + environment.trialGracePeriodDays;

      // =====================
      // ACTIVE TRIAL
      // =====================
      if (daysLive < graceStart) {
         this.trialPhase = 'TRIAL';
         this.daysRemaining = environment.trialDays - daysLive;

         // Badge should ALWAYS show during trial
         this.showTrialBadge = true;

         // Banner only shows near trial end
         this.showTrialNotice = daysLive >= environment.trialStartNoticeDay;

         this.showGraceModal = false;
         return;
      }

      // =====================
      // GRACE PERIOD
      // =====================
      if (daysLive >= graceStart && daysLive <= graceEnd) {
         this.trialPhase = 'GRACE';
         this.graceDaysRemaining =
            environment.trialGracePeriodDays - (daysLive - graceStart);

         this.showGraceModal = true; // only controls modal visibility
         this.showTrialBadge = true;
         return;
      }

      // =====================
      // EXPIRED
      // =====================
      this.trialPhase = 'EXPIRED';
      this.auth.logout({
         logoutParams: { returnTo: this.document.location.origin }
      });
   }

   private resetTrialUi() {
      this.showTrialBadge = false;
      this.showTrialNotice = false;
      this.showGraceModal = false;
   }

   get isGracePeriod(): boolean {
      return this.trialPhase == 'GRACE';
   }

   dismissTrialNotice() {
      this.showTrialNotice = false;
      localStorage.setItem('trialNoticeDismissed', 'true');
   }

   goToBillingSetup() {
      this.showGraceModal = false;
      this.router.navigate([environment.supportLinks.billingSetup]);
   }

   closeGraceModal() {
      this.showGraceModal = false;
   }

   onDeclineContinue() {
      console.log('User will not continue with Tyalls.');
   }

   createMenus(): void {
      this.mainMenuItems = [
         {
            label: 'Dashboard',
            icon: 'pi pi-home',
            routerLink: ['/dashboard'],
            visible: true
         },
         {
            label: 'Launch',
            icon: 'pi pi-play',
            visible: true,
            items: [
               {
                  label: 'Auto Launch',
                  icon: 'pi pi-play',
                  routerLink: ['/taskboard']
               },
               {
                  label: 'Manual Launch',
                  icon: 'pi pi-directions',
                  routerLink: ['/workflow/launch']
               }
            ]
         },
         {
            label: 'Reports',
            icon: 'pi pi-chart-line',
            visible: true,
            items: [
               {
                  label: 'Portfolio Snapshot',
                  icon: 'pi pi-chart-bar',
                  routerLink: ['/report/portfolio']
               },
               {
                  label: 'Audit Report',
                  icon: 'pi pi-table',
                  routerLink: ['/report/audit']
               },
               {
                  label: 'Task Calendar',
                  icon: 'pi pi-calendar',
                  routerLink: ['/tasks']
               },
               {
                  label: 'Task Analysis',
                  icon: 'pi pi-check-square',
                  routerLink: ['/report/tasks'],
                  visible: this.userPermissions.reports.read
               },
               {
                  label: 'Groups',
                  icon: 'pi pi-users',
                  routerLink: ['/report/groups'],
                  visible: this.userPermissions.reports.beta
               },
               {
                  label: 'Team Analysis',
                  icon: 'pi pi-chart-bar',
                  routerLink: ['/report/staff'],
                  visible: this.userPermissions.reports.read
               }
            ]
         },
         {
            label: 'Admin',
            icon: 'pi pi-cog',
            visible:
               this.userPermissions.user.read ||
               this.userPermissions.groups.read ||
               this.userPermissions.taskManagement.read,
            items: [
               {
                  label: 'Configuration',
                  icon: 'pi pi-list',
                  routerLink: ['/property/admin/tyalls-configuration'],
                  visible:
                     this.userPermissions.user.read ||
                     this.userPermissions.groups.read ||
                     this.userPermissions.taskManagement.read
               },
               {
                  label: 'Billing',
                  icon: 'pi pi-credit-card',
                  routerLink: ['/property/admin/billing-page'],
                  visible: this.userPermissions.isAccountOwner
               }
               /*               {
                  label: 'Account Management',
                  icon: 'pi pi-briefcase',
                  routerLink: ['/admin/account-management'],
                  visible: this.userPermissions.isAccountOwner
               }*/
            ]
         },
         {
            label: 'App Admin',
            icon: 'pi pi-sliders-h',
            visible: this.userPermissions.user.super,
            items: [
               {
                  label: 'Form Creator',
                  icon: 'pi pi-file',
                  routerLink: ['/admin/form'],
                  visible: this.userPermissions.forms.super
               },
               {
                  label: 'User Admin',
                  icon: 'pi pi-user',
                  routerLink: ['/admin/user'],
                  visible: this.userPermissions.user.super
               },
               {
                  label: 'New Signups',
                  icon: 'pi pi-building',
                  routerLink: ['/admin/new-signups-management'],
                  visible: this.userPermissions.user.super
               }
            ]
         },
         {
            label: this.userEmail,
            icon: 'pi pi-user',
            items: [
               {
                  label: 'My Profile',
                  icon: 'pi pi-id-card',
                  routerLink: ['/my-profile']
               },
               {
                  label: 'Support Hub',
                  icon: 'pi pi-ticket',
                  url: environment.externalLinks.supportHub,
                  target: '_blank'
               },
               {
                  label: 'Sign Out',
                  icon: 'pi pi-sign-out',
                  command: () => this.signOut()
               }
            ]
         }
      ];
   }

   handleOutstandingTasksClick(): void {
      if (this.outstandingTaskList.length > 0 && this.currentProperty) {
         TaskUtils.goToTask(
            this.outstandingTaskList[0],
            this.router,
            this.currentProperty
         );
      } else {
         this.router.navigate(['/dashboard'], {
            queryParams: { tab: 'outstanding' }
         });
      }
   }

   handleGotoSearchClick(): void {
      this.router.navigate(['/search']);
   }

   signOut() {
      if (confirm('Are you sure you want to sign out?')) {
         this.auth.logout({
            logoutParams: {
               returnTo: this.document.location.origin
            }
         });
      }
   }

   private tryCreateMenus(): void {
      if (this.userEmail && this.hasLoadedPermissions) {
         this.createMenus();
      }
   }
}
